import AOS from "aos";
import "aos/dist/aos.css";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { fetchData, toAbsoluteMedia } from "../../api/api";
import "./LatestProjects3D.css";

function toDateValue(v) {
  if (!v) return 0;
  const d = new Date(v);
  const t = d.getTime();
  return Number.isFinite(t) ? t : 0;
}

function projectKindFromCategory(category) {
  const c = String(category || "").toLowerCase();
  if (c.includes("mobile")) return "mobile";
  return "web";
}

function normalizeProject(p, index) {
  const category = p?.category || p?.type || p?.project_type || p?.projectType || "";
  const kind = projectKindFromCategory(category);
  return {
    id: p?.id ?? index,
    title: p?.title || "",
    description: p?.short_description || p?.shortDescription || "",
    link: p?.project_link || p?.link || "#",
    image: toAbsoluteMedia(p?.image || p?.img || ""),
    category,
    kind, // "web" | "mobile"
    createdAt:
      toDateValue(p?.created_at) ||
      toDateValue(p?.createdAt) ||
      toDateValue(p?.updated_at) ||
      toDateValue(p?.updatedAt) ||
      0,
  };
}

function compareProjects(a, b) {
  // newest first: createdAt → id (numeric) → stable
  if (a.createdAt !== b.createdAt) return b.createdAt - a.createdAt;
  const an = Number(a.id);
  const bn = Number(b.id);
  if (Number.isFinite(an) && Number.isFinite(bn) && an !== bn) return bn - an;
  return 0;
}

function asListResponse(raw) {
  if (Array.isArray(raw)) return raw;
  if (raw && Array.isArray(raw.results)) return raw.results;
  return [];
}

async function fetchLatestProjects() {
  // Try common DRF ordering params first; fall back if unsupported.
  const attempts = [
    { ordering: "-created_at" },
    { ordering: "-updated_at" },
    { ordering: "-id" },
    null,
  ];

  let lastErr;
  for (const params of attempts) {
    try {
      return await fetchData("/projects/list/", params ? { params } : undefined);
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr || new Error("Failed to load projects");
}

function ProjectCard3D({ project, variant = "grid" }) {
  const cardRef = useRef(null);
  const rafRef = useRef(0);

  const applyTilt = (clientX, clientY) => {
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const px = x / rect.width - 0.5;
    const py = y / rect.height - 0.5;

    const max = variant === "featured" ? 10 : 12;
    const ry = px * max;
    const rx = -py * max;

    // Also drive highlight position
    const mx = Math.max(0, Math.min(100, (x / rect.width) * 100));
    const my = Math.max(0, Math.min(100, (y / rect.height) * 100));

    el.style.setProperty("--rx", `${rx.toFixed(2)}deg`);
    el.style.setProperty("--ry", `${ry.toFixed(2)}deg`);
    el.style.setProperty("--mx", `${mx.toFixed(2)}%`);
    el.style.setProperty("--my", `${my.toFixed(2)}%`);
  };

  const onPointerMove = (e) => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) return;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    const { clientX, clientY } = e;
    rafRef.current = requestAnimationFrame(() => applyTilt(clientX, clientY));
  };

  const onPointerLeave = () => {
    const el = cardRef.current;
    if (!el) return;
    el.style.setProperty("--rx", "0deg");
    el.style.setProperty("--ry", "0deg");
    el.style.setProperty("--mx", "50%");
    el.style.setProperty("--my", "50%");
  };

  const content = (
    <div
      ref={cardRef}
      className={`lp3d-card lp3d-card--${variant}`}
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
    >
      <div className="lp3d-media">
        <img
          className="lp3d-img"
          src={project.image || "/placeholder.svg"}
          alt={project.title || "Project"}
          onError={(e) => {
            e.currentTarget.src = "/placeholder.svg";
          }}
        />
      </div>

      <div className="lp3d-meta">
        <p className={`lp3d-kicker${project.description ? "" : " lp3d-kicker--empty"}`}>
          {(project.kind === "mobile" ? "Mobile App" : "Website") +
            (project.description ? ` • ${project.description}` : "")}
        </p>
        <h3 className="lp3d-title">{project.title || "Untitled Project"}</h3>
        <span className="lp3d-cta">View Project</span>
      </div>
    </div>
  );

  const isExternal = /^https?:\/\//i.test(project.link);
  return isExternal ? (
    <a
      href={project.link}
      target="_blank"
      rel="noopener noreferrer"
      className="lp3d-link"
    >
      {content}
    </a>
  ) : (
    <Link to={project.link} className="lp3d-link">
      {content}
    </Link>
  );
}

export default function LatestProjects3D() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    AOS.init({ duration: 1000 });
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const raw = await fetchLatestProjects();
        const list = asListResponse(raw);
        const normalized = list.map(normalizeProject);
        normalized.sort(compareProjects);
        if (!alive) return;
        setProjects(normalized);
      } catch (e) {
        if (!alive) return;
        setErr("Failed to load projects. Please try again.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const items = useMemo(() => projects.filter((p) => p.title || p.image), [projects]);

  const { featured, small } = useMemo(() => {
    const sorted = [...items].sort(compareProjects);

    // Requirement: show Website first, and only 1 Mobile App.
    // Featured = latest Website (fallback to latest overall).
    const latestWeb = sorted.find((p) => p.kind === "web") || null;
    const latestOverall = sorted[0] || null;
    const featuredPick = latestWeb || latestOverall;

    // Grid: Web first, include at most one Mobile (latest).
    const latestMobile = sorted.find((p) => p.kind === "mobile") || null;

    const used = new Set();
    const pick = (p) => {
      if (!p) return;
      const key = String(p.id);
      if (used.has(key)) return;
      used.add(key);
      return p;
    };

    const grid = [];
    pick(featuredPick); // mark featured as used

    // Fill grid with web projects first
    for (const p of sorted) {
      if (grid.length >= 3) break;
      if (p.kind !== "web") continue;
      const next = pick(p);
      if (next) grid.push(next);
    }

    // Ensure exactly one mobile project is included (if available), as the last slot.
    if (grid.length < 3) {
      const m = pick(latestMobile);
      if (m) grid.push(m);
    } else {
      // Replace last item with mobile, if mobile exists and not already used.
      const m = pick(latestMobile);
      if (m) grid[grid.length - 1] = m;
    }

    // If still not full (e.g., no web projects), fill with whatever is latest.
    for (const p of sorted) {
      if (grid.length >= 3) break;
      const next = pick(p);
      if (next) grid.push(next);
    }

    return { featured: featuredPick, small: grid };
  }, [items]);

  return (
    <section className="lp3d-section overflow-hidden">
      <div className="container lp3d-container">
        <div className="lp3d-top">
          <div className="lp3d-copy" data-aos="fade-up">
            <p className="lp3d-eyebrow">OUR WORK</p>
            <h2 className="lp3d-heading">
              Experiences <br />
              we have created
            </h2>
            <p className="lp3d-subtext">
              Collaborated with clients to understand their needs, provide
              regular updates, and deliver reliable solutions with first-hand
              insight into how MarichiTechAI’s processes are implemented across
              all projects.
            </p>
          </div>

          <div className="lp3d-featured" data-aos="fade-up" data-aos-delay="100">
            {!loading && !err && featured ? (
              <ProjectCard3D project={featured} variant="featured" />
            ) : (
              <div className="lp3d-skeleton lp3d-skeleton--featured" />
            )}
          </div>
        </div>

        <div className="lp3d-grid" data-aos="fade-up" data-aos-delay="150">
          {loading && (
            <>
              <div className="lp3d-skeleton" />
              <div className="lp3d-skeleton" />
              <div className="lp3d-skeleton" />
            </>
          )}

          {!loading && err && <p className="lp3d-error">{err}</p>}

          {!loading && !err && small.length === 0 && !featured && (
            <p className="lp3d-empty">No projects available.</p>
          )}

          {!loading &&
            !err &&
            small.map((p) => <ProjectCard3D key={p.id} project={p} />)}
        </div>

        <div className="lp3d-bottom" data-aos="fade-up" data-aos-delay="200">
          <Link to="/portfolio" className="lp3d-viewall">
            <span>View All Works</span>
            <span className="lp3d-arrow" aria-hidden="true">
              →
            </span>
          </Link>
        </div>
      </div>
    </section>
  );
}

